<template>
  <section class="introduction-item show-on-load" :class="mainClasses">
    <div class="container">
      <div class="introduction-item__row row">
        <div
          v-if="isBookingWidget && isBookingWidgetTopMobile"
          class="introduction-item__booking-widget-wrapper col-12 col-md-6 col-lg-5 col-xl-4 col-xxl-3"
        >
          <sc-placeholder name="introduction-placeholder" :rendering="rendering" />
        </div>
        <div
          class="introduction-item__texts-wrapper col-12 col-md-6"
          :class="{
            'col-lg-6 col-xl-7': isOneOpeningHoursItem,
            'col-lg-7': isBookingWidget,
            'col-lg-5': !isOneOpeningHoursItem && !isBookingWidget,
            ...detectComponentVariant(variantedComponents.INTRODUCTION_ITEM),
          }"
        >
          <div class="introduction-item__texts">
            <div class="introduction-item__header-wrapper">
              <sc-text
                class="introduction-item__header"
                :class="headerClasses"
                :tag="params?.headingType || 'h2'"
                :field="fields.title"
              />
              <div v-if="locationKey || rawVenueId" class="introduction-item__venue-info-btn">
                <venue-info-widget :location-id="locationKey" :venue-id="rawVenueId" />
              </div>
            </div>
            <p class="introduction-item__description">
              <span>
                {{ introductionItemDescription }}
              </span>
              <button
                v-if="showReadMore"
                :aria-expanded="isShowMoreOpened"
                class="introduction-item__description-more link"
                @click="handleClickShowMore"
              >
                {{ isShowMoreOpened ? t('introduction-show-less') : t('introduction-show-more') }}
              </button>
            </p>

            <div v-if="cta1Text || cta2Text" class="introduction-item__buttons">
              <base-button
                v-if="cta1Text"
                data-test-id="introduction-cta1"
                :label="cta1Text"
                type="link"
                :link="fields.cta1.value"
                variant="primary"
                @click="handleActionClick(cta1Text)"
              />
              <base-button
                v-if="cta2Text"
                data-test-id="introduction-cta2"
                :label="cta2Text"
                type="link"
                :link="fields.cta2.value"
                variant="secondary"
                @click="handleActionClick(cta2Text)"
              />
            </div>
          </div>
        </div>
        <div
          v-if="openingHoursVisible"
          class="introduction-item__opening-hours-wrapper col-12 col-md-6"
          :class="[
            isOneOpeningHoursItem && 'col-xl-5',
            isBookingWidget && 'col-lg-7 col-xl-7',
            !isOneOpeningHoursItem && !isBookingWidget && 'col-lg-7',
          ]"
        >
          <div
            class="introduction-item__opening-hours"
            :class="{
              'introduction-item__opening-hours--single-item': isOneOpeningHoursItem,
              ...detectComponentVariant(variantedComponents.OPENING_HOURS),
            }"
          >
            <slot name="admissionTypes" :heading-tag="openingHoursHeadingTag" />
            <opening-hours
              v-if="fields?.openingHours?.value?.length"
              :title="dictionary.openingHours"
              :icon="'Clock'"
              :opening-hours="fields.openingHours"
              :description="openingHoursDescription"
              :is-advanced-notice="fields.specialHoursEnabled?.value"
              :heading-tag="openingHoursHeadingTag"
            />
            <opening-hours
              v-if="fields?.servingHours?.value?.length"
              :title="dictionary.servingHours"
              :icon="'Cutlry'"
              :opening-hours="fields.servingHours"
              :is-advanced-notice="fields.specialHoursEnabled?.value"
              :heading-tag="openingHoursHeadingTag"
            />
          </div>
        </div>
        <div
          v-if="
            (isBookingWidget && !isBookingWidgetTopMobile) || $jss.sitecoreContext().pageEditing
          "
          class="introduction-item__booking-widget-wrapper col-12 col-md-6 col-lg-5 col-xl-4 col-xxl-3"
        >
          <sc-placeholder name="introduction-placeholder" :rendering="rendering" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { Text as ScText, Placeholder as ScPlaceholder } from '@sitecore-jss/sitecore-jss-vue';
import BaseButton from '../2_elements/BaseButton/BaseButton.vue';
import OpeningHours from '../2_elements/OpeningHoursItem/OpeningHoursItem.vue';
import VenueInfoWidget from '../2_elements/VenueInfoWidget/VenueInfoWidget.vue';
import { useI18n } from 'vue-i18n';
import { contentButtonsDataLayer } from '../../data-layer/contentButtons';
import { useDetectVariantByBrand, variantedComponents } from '../../utils/componentVariants';
import { useButtonsAndLinksTracking } from '../../data-layer/hooks/useButtonsAndLinksTracking.js';
import useMedia from '../../utils/useMedia.js';

const { trackButtonState } = useButtonsAndLinksTracking({ componentName: 'introduction_item' });

const detectComponentVariant = useDetectVariantByBrand();
const { t } = useI18n();
const { isMedia: isMobile } = useMedia('(max-width: 768px)');

const props = defineProps({
  fields: {
    type: Object,
    default: () => ({}),
  },
  rendering: {
    type: Object,
  },
  params: {
    type: Object,
  },
  showOpeningHours: {
    type: Boolean,
    default: false,
  },
  openingHoursDescription: {
    type: String,
  },
  admission: {
    type: Boolean,
    default: false,
  },
});

const DESCRIPTION_MAX_VISIBLE_LENGTH = 260;

const jssStore = inject('jssStore');
const sitecoreContext = jssStore?.sitecoreContext() || {};
const brandName = sitecoreContext.brandName || null;
const isGuestlineUrl = !!sitecoreContext.guestlineUrl;
const rawVenueId = sitecoreContext.rawVenueId;
const locationKey = sitecoreContext.locationKey;

const isShowMoreOpened = ref(false);
const showReadMore = ref(props.fields.description?.value?.length > DESCRIPTION_MAX_VISIBLE_LENGTH);

const isBookingWidget = computed(() => {
  const placeholder = props.rendering?.placeholders?.['introduction-placeholder'];
  const isBookATable = placeholder?.some(
    (component) =>
      ['OpenTableWidget', 'BookingWidget'].includes(component.componentName) &&
      component.fields &&
      Object.keys(component.fields).length
  );
  const isBookARoom =
    placeholder?.some((component) => component.componentName === 'BookingWidget') && isGuestlineUrl;

  return isBookATable || isBookARoom;
});

const mainClasses = computed(() => ({
  'introduction-item--only-texts': !openingHoursVisible.value && !isBookingWidget.value,
  'bottom-paper-border': brandName === 'ChefBrewer',
  'introduction-item--booking-widget': isBookingWidget.value,
}));

const headerClasses = computed(() => [brandName === 'Belhaven' ? 'font-xl' : 'font-l']);
const cta1Text = computed(() => props.fields.cta1?.value?.text);
const cta2Text = computed(() => props.fields.cta2?.value?.text);

const introductionItemDescription = computed(() =>
  !isShowMoreOpened.value && showReadMore.value
    ? `${props.fields.description?.value?.slice(0, DESCRIPTION_MAX_VISIBLE_LENGTH)}...`
    : props.fields.description?.value
);

const dictionary = computed(() => {
  const specialHrs = props.fields.specialHoursEnabled?.value;
  return {
    openingHours: t(
      specialHrs ? 'special-hours-advanced-notice-festive-opening-hours' : 'opening-hours'
    ),
    servingHours: t(specialHrs ? 'special-hours-advanced-notice-kitchen-hours' : 'serving-hours'),
  };
});

const openingHoursHeadingTag = computed(() => (props.params?.headingType === 'h1' ? 'h2' : 'h3'));

const isBookingWidgetTopMobile = computed(
  () => isMobile.value && props.params?.bookingWidgetMobilePosition === 'top'
);

const handleClickShowMore = () => {
  handleActionClick(
    !isShowMoreOpened.value
      ? contentButtonsDataLayer.actions.SHOW_MORE
      : contentButtonsDataLayer.actions.SHOW_LESS
  );
  isShowMoreOpened.value = !isShowMoreOpened.value;
};

const openingHoursVisible = computed(
  () =>
    props.fields.openingHours?.value.length ||
    props.fields.servingHours?.value.length ||
    props.showOpeningHours ||
    props.admission
);

const isOneOpeningHoursItem = computed(
  () =>
    !props.admission &&
    !isBookingWidget.value &&
    (!props.fields.openingHours?.value?.length || !props.fields.servingHours?.value?.length)
);

const handleActionClick = (action) => {
  trackButtonState(action);
};
</script>

<style lang="scss">
@import './scss/IntroductionItem.scss';
</style>
